import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { ModuleGraphType } from 'module/page/graph/model/module-graph.type';
import { ModuleGraph } from 'module/page/graph/module-graph';
import { ModuleGraphI18n } from 'module/page/graph/module-graph-i18n';

import { AppLocator } from '../../../../app.locator';

export interface ModuleGraphI18nDocument {
	readonly title: string;
	readonly locale: string;
	readonly labels: string;
	readonly legends: string;
	readonly descriptions: string;
}

export interface ModuleGraphDocument {
	readonly id: string;
	readonly type: string;
	readonly colors: string;
	readonly formula: string;
	readonly moduleId: string;
	readonly updateTimestamp: number;
	readonly localizations: ReadonlyArray<ModuleGraphI18nDocument>;
}

export type ModuleGraphState = EntityState<ModuleGraphDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'moduleGraphs' })
export class ModuleGraphStore extends EntityStore<ModuleGraphState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(graph: ModuleGraph): ModuleGraphDocument {
		return graph
			? {
					id: graph.id,
					type: graph.type,
					colors: graph.colors,
					formula: graph.formula,
					moduleId: graph.moduleId,
					updateTimestamp: graph.updateTimestamp,
					localizations: graph.localizations.map((loc) => ({
						descriptions: loc.descriptions,
						legends: loc.legends,
						labels: loc.labels,
						locale: loc.locale,
						title: loc.title,
					})),
			  }
			: null;
	}

	public asEntity(document: ModuleGraphDocument): ModuleGraph {
		const localizations = !!document
			? document.localizations.map(
					(loc) =>
						new ModuleGraphI18n(
							loc.title,
							loc.locale,
							loc.labels,
							loc.legends,
							loc.descriptions
						)
			  )
			: [];

		return document
			? new ModuleGraph(
					document.id,
					document.colors,
					document.formula,
					document.moduleId,
					ModuleGraphType[document.type],
					document.updateTimestamp,
					localizations,
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
